import { styled } from '@mui/material/styles';
import { Play as PlayIcon } from 'lucide-react';
import type { ImgHTMLAttributes, Ref } from 'react';

import { posterGradient } from '@/config/referenceData';
import { cn } from '@/lib/cn';
import { ICON_SIZE } from './iconSize';

const FIT = { cover: 'object-cover', contain: 'object-contain' } as const;

const RADIUS = { none: '', xs: 'rounded-md', sm: 'rounded-lg' } as const;

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  ref?: Ref<HTMLImageElement>;
  /** `cover` fills the box and cuts the edges; `contain` shows the whole image. */
  fit?: keyof typeof FIT;
  radius?: keyof typeof RADIUS;
}

/** Shows an image with a kit fit and corner. Its size comes from `className`, and `alt` is empty by default. */
export function Image({ fit, radius = 'none', alt = '', className, ref, ...props }: ImageProps) {
  return <img ref={ref} alt={alt} className={cn(fit && FIT[fit], RADIUS[radius], className)} {...props} />;
}

export type PosterSize = 'sm' | 'md' | 'lg' | 'banner' | 'fill';

const SIZES: Record<PosterSize, string> = {
  sm: 'h-9 w-16',
  md: 'h-11 w-20',
  lg: 'h-14 w-24',
  banner: 'h-28 w-full',
  fill: 'aspect-video w-full',
};

const Root = styled('span', { name: 'KitMediaPoster', slot: 'root' })({});
const Play = styled('span', { name: 'KitMediaPoster', slot: 'play' })({});
const Duration = styled('span', { name: 'KitMediaPoster', slot: 'duration' })({});

export interface MediaPosterProps {
  gradientIndex: number;
  size?: PosterSize;
  duration?: string;
  playable?: boolean;
  className?: string;
}

/** Shows a gradient poster, with an optional play mark and duration. */
export function MediaPoster({
  gradientIndex,
  size = 'md',
  duration,
  playable = false,
  className,
}: MediaPosterProps) {
  return (
    <Root
      aria-hidden
      style={{ backgroundImage: posterGradient(gradientIndex) }}
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg',
        SIZES[size],
        className,
      )}
    >
      {playable && (
        <Play className="flex h-9 w-9 items-center justify-center rounded-full">
          <PlayIcon size={ICON_SIZE.md} className="translate-x-px" />
        </Play>
      )}
      {duration && (
        <Duration className="absolute bottom-1 right-1 rounded-sm px-1.5 py-0.5">{duration}</Duration>
      )}
    </Root>
  );
}

const Video = styled('video', { name: 'KitVideoPlayer', slot: 'root' })({});

export interface VideoPlayerProps {
  src: string;
  /** Accessible name for the player. */
  label: string;
  className?: string;
}

/** Shows one video with the controls of the browser. The browser must be able to
 *  decode the source. */
export function VideoPlayer({ src, label, className }: VideoPlayerProps) {
  return (
    <Video
      key={src}
      src={src}
      controls
      playsInline
      preload="metadata"
      aria-label={label}
      className={cn('max-h-video w-full rounded-lg', className)}
    />
  );
}
